import { FormEvent, useState } from 'react'
import { HeartPulse, Languages, Lock, Stethoscope, User, UserPlus } from 'lucide-react'
import { t, useLang, toggleLang } from '../lib/i18n'

/**
 * Login page with role selection (Patient vs Provider) and admin:admin auth.
 *
 * Bilingual EN/AR via the shared i18n store. On successful login, calls
 * `onLogin(role)` so the parent can route to the appropriate experience.
 * The language toggle in the corner lets users switch before signing in.
 * The NHI-registration link is a plain anchor (full page load) so App's
 * `/nhi/register` standalone-render check sees the new path — client-side
 * navigation would not re-evaluate it.
 */

type Role = 'patient' | 'provider'

export function LoginPage({
  onLogin,
}: {
  onLogin: (role: Role) => void
}) {
  useLang() // re-render on language switch
  const [role, setRole] = useState<Role>('patient')
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')

  async function submit(event: FormEvent) {
    event.preventDefault()
    setError('')
    setSubmitting(true)
    // Simulate a brief network round-trip for UX realism.
    await new Promise((r) => setTimeout(r, 350))
    if (username.trim() === 'admin' && password === 'admin') {
      onLogin(role)
    } else {
      setError(t('login.error'))
    }
    setSubmitting(false)
  }

  const roles: { key: Role; icon: typeof User; label: string; desc: string }[] = [
    { key: 'patient', icon: User, label: t('login.role.patient'), desc: t('login.role.patientDesc') },
    { key: 'provider', icon: Stethoscope, label: t('login.role.provider'), desc: t('login.role.providerDesc') },
  ]

  return (
    <div className="min-h-screen flex flex-col bg-stone-50">
      {/* Language toggle */}
      <div className="flex justify-end p-4">
        <button
          type="button"
          onClick={toggleLang}
          aria-label={t('login.langAria')}
          className="inline-flex items-center gap-2 rounded-lg border border-stone-300 bg-white px-3 py-1.5 text-sm font-medium text-stone-700 hover:bg-stone-100 transition-colors"
        >
          <Languages className="h-4 w-4" />
          {t('shell.arabicLabel')}
        </button>
      </div>

      {/* Card */}
      <div className="flex flex-1 items-center justify-center px-4 pb-12">
        <div className="w-full max-w-md">
          {/* Brand */}
          <div className="mb-8 text-center">
            <div className="mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-2xl bg-emerald-600 text-white">
              <HeartPulse className="h-7 w-7" />
            </div>
            <h1 className="text-2xl font-bold text-stone-900">{t('login.title')}</h1>
            <p className="mt-1 text-sm text-stone-500">{t('login.subtitle')}</p>
          </div>

          {/* Role selection */}
          <div className="mb-6">
            <p className="mb-3 text-sm font-medium text-stone-700">{t('login.rolePrompt')}</p>
            <div className="grid grid-cols-2 gap-3">
              {roles.map(({ key, icon: Icon, label, desc }) => {
                const selected = role === key
                return (
                  <button
                    key={key}
                    type="button"
                    onClick={() => setRole(key)}
                    aria-pressed={selected}
                    className={`flex flex-col items-center gap-2 rounded-xl border p-4 text-center transition-colors ${
                      selected
                        ? 'border-emerald-600 bg-emerald-50 ring-2 ring-emerald-600/20'
                        : 'border-stone-200 bg-white hover:border-stone-300'
                    }`}
                  >
                    <Icon className={`h-7 w-7 ${selected ? 'text-emerald-700' : 'text-stone-400'}`} />
                    <span className={`text-sm font-semibold ${selected ? 'text-emerald-800' : 'text-stone-700'}`}>
                      {label}
                    </span>
                    <span className="text-xs text-stone-500 leading-snug">{desc}</span>
                  </button>
                )
              })}
            </div>
          </div>

          {/* Form */}
          <form onSubmit={submit} className="space-y-4 rounded-2xl border border-stone-200 bg-white p-6 shadow-sm">
            <div>
              <label htmlFor="login-username" className="mb-1.5 block text-sm font-medium text-stone-700">
                {t('login.username')}
              </label>
              <div className="relative">
                <User className="pointer-events-none absolute start-3 top-1/2 h-4 w-4 -translate-y-1/2 text-stone-400" />
                <input
                  id="login-username"
                  type="text"
                  autoComplete="username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder={t('login.usernamePlaceholder')}
                  className="w-full rounded-lg border border-stone-300 bg-white py-2.5 ps-10 pe-3 text-sm text-stone-900 placeholder:text-stone-400 focus:border-emerald-600 focus:outline-none focus:ring-2 focus:ring-emerald-600/20"
                />
              </div>
            </div>

            <div>
              <label htmlFor="login-password" className="mb-1.5 block text-sm font-medium text-stone-700">
                {t('login.password')}
              </label>
              <div className="relative">
                <Lock className="pointer-events-none absolute start-3 top-1/2 h-4 w-4 -translate-y-1/2 text-stone-400" />
                <input
                  id="login-password"
                  type="password"
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder={t('login.passwordPlaceholder')}
                  className="w-full rounded-lg border border-stone-300 bg-white py-2.5 ps-10 pe-3 text-sm text-stone-900 placeholder:text-stone-400 focus:border-emerald-600 focus:outline-none focus:ring-2 focus:ring-emerald-600/20"
                />
              </div>
            </div>

            {error && (
              <p role="alert" className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">
                {error}
              </p>
            )}

            <button
              type="submit"
              disabled={submitting}
              className="w-full rounded-lg bg-emerald-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-emerald-600/30 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {submitting ? t('login.signingIn') : t('login.submit')}
            </button>
          </form>

          <a
            href="/nhi/register"
            className="mt-4 flex items-center justify-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-2.5 text-sm font-semibold text-emerald-800 shadow-sm transition-colors hover:bg-emerald-100 focus:outline-none focus:ring-2 focus:ring-emerald-600/30 focus:ring-offset-2"
          >
            <UserPlus className="h-4 w-4" />
            {t('login.noNhi')}{' '}
            <span className="underline underline-offset-2">{t('login.registerNhi')}</span>
          </a>

          <p className="mt-6 text-center text-xs text-stone-400">{t('login.footer')}</p>
        </div>
      </div>
    </div>
  )
}